/**
 * Car stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const carStickersPack: ToilePack = {
    slug: 'car-stickers',
    category: 'sticker',
    name: 'Car stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'van', label: 'Vintage van', subject: 'a vintage camper van in profile'},
        {key: 'sports', label: 'Sports car', subject: 'a low sports car in profile'},
        {key: 'badge', label: 'Garage badge', subject: 'a round badge lettered "GARAGE" with a wrench'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a motorcycle instead', 'a whole sticker sheet', 'in neon colors'],
    directionHint: 'try: a motorcycle instead',
    seo: {
        title: 'Car Stickers: Vintage Van, Sports Car, Garage',
        metaDescription: 'Nine car stickers — a vintage camper van, a sports car in profile, a lettered garage badge — in retro, bold cartoon, and minimal line styles.',
        h1: 'Car stickers: vintage van, sports car, garage badge',
        tagline: 'Nine stickers, three car icons, three styles each.',
        intro: "A vintage camper van in profile, a low sports car in profile, and a round badge lettered 'GARAGE' with a wrench — three car stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a pickup truck instead, a roadtrip scene, or a gas-station composition, and the same three styles carry it.",
        useCases: [
            'a car bumper or rear window',
            'a laptop or water bottle',
            'a toolbox or garage door',
            'a gift for a car enthusiast',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine car stickers — a vintage camper van, a sports car, and a garage badge — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a pickup truck or a different car?', a: 'Yes — the direction field takes any variation, including a pickup truck instead, a roadtrip scene, or a gas-station composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a bumper-sized print.'},
            {q: 'Do these show a real car brand or model?', a: 'No — every vehicle is a generic silhouette-style illustration, not a licensed brand or model.'},
        ],
    },
}
